import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  Update as UpdateIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { getRaspiSoftwareVersionCheck, updateRaspiSoftware } from '../../services/api';


interface UpdateRaspiSoftwareDialogProps {
  open: boolean;
  onClose: () => void;
  raspiId: string;
  currentRaspiVersion: string;
}

interface UpdateStatus {
  status: 'CHECKING' | 'READY' | 'UPDATING' | 'COMPLETED' | 'FAILED';
  progress: number;
  currentStep: string;
  errorMessage?: string;
}

export const UpdateRaspiSoftwareDialog: React.FC<UpdateRaspiSoftwareDialogProps> = ({
  open,
  onClose,
  raspiId,
}) => {
  const [versionInfo, setVersionInfo] = useState<{ current: string; available: string; needsUpdate: boolean } | null>(null);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>({
    status: 'CHECKING',
    progress: 0,
    currentStep: 'Checking versions...'
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [showUpdateWarning, setShowUpdateWarning] = useState(false);

  useEffect(() => {
    if (open) {
      checkVersions();
    }
    // eslint-disable-next-line
  }, [open, raspiId]);

  const checkVersions = async () => {
    setUpdateStatus({
      status: 'CHECKING',
      progress: 0,
      currentStep: 'Checking available software version...'
    });
    try {
      const result = await getRaspiSoftwareVersionCheck(raspiId);
      setVersionInfo({
        current: result.current_version,
        available: result.new_version,
        needsUpdate: result.update_needed
      });
      setUpdateStatus({
        status: 'READY',
        progress: 0,
        currentStep: result.update_needed ? 'Update available' : 'Software is up to date'
      });
    } catch (error) {
      setUpdateStatus({
        status: 'FAILED',
        progress: 0,
        currentStep: 'Version check failed',
        errorMessage: error instanceof Error ? error.message : 'Failed to check version'
      });
    }
  };

  const handleStartUpdateClick = () => {
    if (!versionInfo?.needsUpdate) return;
    setShowUpdateWarning(true);
  };

  const handleConfirmUpdate = async () => {
    setIsUpdating(true);
    setUpdateStatus({
      status: 'UPDATING',
      progress: 0,
      currentStep: 'Starting software update...'
    });
    try {
      await updateRaspiSoftware(raspiId);
      setUpdateStatus({
        status: 'COMPLETED',
        progress: 100,
        currentStep: 'Update completed successfully'
      });
      setVersionInfo((prev) => prev ? { ...prev, current: prev.available, needsUpdate: false } : prev);
    } catch (error) {
      setUpdateStatus({
        status: 'FAILED',
        progress: 0,
        currentStep: 'Update failed',
        errorMessage: error instanceof Error ? error.message : 'Update failed'
      });
    }
    setIsUpdating(false);
    setShowUpdateWarning(false);
  };

  const handleCancel = () => {
    if (isUpdating) {
      // Optionally implement cancel logic
    }
    onClose();
  };

  const getStatusColor = (status: UpdateStatus['status']) => {
    switch (status) {
      case 'COMPLETED': return 'success.main';
      case 'FAILED': return 'error.main';
      case 'UPDATING': return 'info.main';
      case 'READY': return 'text.secondary';
      default: return 'text.secondary';
    }
  };

  const getStatusIcon = (status: UpdateStatus['status']) => {
    switch (status) {
      case 'COMPLETED': return <CheckCircleIcon color="success" />;
      case 'FAILED': return <ErrorIcon color="error" />;
      case 'UPDATING': return <UpdateIcon color="info" />;
      case 'READY': return <InfoIcon color="primary" />;
      default: return <InfoIcon color="primary" />;
    }
  };

  const isUpdateAvailable = versionInfo?.needsUpdate;
  const isUpToDate = versionInfo && !versionInfo.needsUpdate;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UpdateIcon color="primary" />
          <Typography variant="h6">Update RASPI Software</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            ({raspiId})
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Version Information */}
          {versionInfo && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Version Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Current Version:
                  </Typography>
                  <Chip 
                    label={versionInfo.current} 
                    color="default"
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Available Version:
                  </Typography>
                  <Chip 
                    label={versionInfo.available} 
                    color={versionInfo.needsUpdate ? "primary" : "success"}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          )}

          <Divider />

          {/* Status Display */}
          {updateStatus.status !== 'CHECKING' && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {getStatusIcon(updateStatus.status)}
                <Typography variant="subtitle2" color={getStatusColor(updateStatus.status)}>
                  {updateStatus.currentStep}
                </Typography>
              </Box>
              
              {updateStatus.status === 'UPDATING' && (
                <>
                  <LinearProgress 
                    variant="determinate" 
                    value={updateStatus.progress} 
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Progress: {updateStatus.progress}%
                  </Typography>
                </>
              )}
              
              {updateStatus.errorMessage && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {updateStatus.errorMessage}
                </Alert>
              )}
            </Box>
          )}

          {/* Update Available Warning */}
          {isUpdateAvailable && (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ⚠️ Software Update Available
              </Typography>
              <Typography variant="body2">
                A new version of the RASPI software is available. Updating will:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                <Typography component="li" variant="body2">
                  Copy new software files to the RASPI device
                </Typography>
                <Typography component="li" variant="body2">
                  Restart the RASPI software to load the new version
                </Typography>
                <Typography component="li" variant="body2">
                  Temporarily disconnect the device during restart
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                The device will automatically reconnect after the update.
              </Typography>
            </Alert>
          )}

          {/* Up to Date Message */}
          {isUpToDate && (
            <Alert 
              severity="success" 
              icon={<CheckCircleIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                ✅ Software is Up to Date
              </Typography>
              <Typography variant="body2">
                The RASPI device is running the latest available software version ({versionInfo.current}).
              </Typography>
            </Alert>
          )}

          {/* Update Information */}
          {isUpdateAvailable && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'info.light', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Update Process
              </Typography>
              <Typography variant="body2">
                The update will copy files from your local RASPI repository to the device and restart the software. 
                This process typically takes 1-2 minutes and the device will be temporarily unavailable.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleCancel} 
          disabled={isUpdating}
          startIcon={<CancelIcon />}
        >
          {isUpdating ? 'Cancel' : 'Close'}
        </Button>
        {isUpdateAvailable && !showUpdateWarning && (
          <Button
            onClick={handleStartUpdateClick}
            disabled={!isUpdateAvailable || isUpdating}
            variant="contained"
            color="primary"
            startIcon={<UpdateIcon />}
          >
            {isUpdating ? 'Updating...' : 'Update Software'}
          </Button>
        )}
        {showUpdateWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ Are you sure you want to update the RASPI software?
            </Typography>
            <Typography variant="body2">
              This will overwrite the current software and restart the device. Proceed?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button onClick={() => setShowUpdateWarning(false)} color="inherit">Cancel</Button>
              <Button onClick={handleConfirmUpdate} color="primary" variant="contained">Confirm Update</Button>
            </Box>
          </Alert>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpdateRaspiSoftwareDialog; 